<template>
  <CModal
    :show.sync="showModal"
    :close-on-backdrop="false"
    title="Create Tier Group"
    @update:show="value => !value && closeModal()"
  >
    <CForm @submit.prevent>
      <CInput
        v-model="$v.form.name.$model"
        label="Tier Group Name"
        placeholder="Enter name"
        horizontal
        :is-valid="$v.form.name.$dirty ? !$v.form.name.$invalid : null"
        :invalid-feedback="nameInvalidFeedback"
      />

      <CSelect
        label="Status"
        horizontal
        :value.sync="form.status"
        :options="statusCodesForNewEntry"
        placeholder="Please select"
      />

      <hr>

      <p class="mb-2">
        <strong>First category:</strong> {{ categoryName }}
      </p>
      <small class="form-text text-muted mb-3 d-block">
        Set the RPC bounds for this category in the new tier group. At least one bound must be set; if both are set, Min RPC must be less than Max RPC. Leave blank for unbounded.
      </small>

      <CInput
        v-model.number="$v.form.minRpc.$model"
        type="number"
        label="Min RPC"
        placeholder="0 (unbounded)"
        horizontal
      />

      <CInput
        v-model.number="$v.form.maxRpc.$model"
        type="number"
        label="Max RPC"
        placeholder="∞ (unbounded)"
        horizontal
      />

      <div
        v-if="$v.form.$dirty && boundsError"
        class="text-danger small"
      >
        {{ boundsError }}
      </div>
    </CForm>

    <template #footer>
      <ButtonLoading
        color="primary"
        :loading="isLoading"
        :disabled="!canSubmit"
        @click="onSubmit"
      >
        Create &amp; Add Category
      </ButtonLoading>
      <CButton
        color="secondary"
        :disabled="isLoading"
        @click="closeModal"
      >
        Cancel
      </CButton>
    </template>
  </CModal>
</template>

<script>
import { mapState } from 'vuex'
import { required } from 'vuelidate/lib/validators'

export default {
  name: 'CreateTierGroupModal',

  props: {
    show: {
      type: Boolean,
      default: false
    },
    categoryId: {
      type: String,
      required: true
    },
    categoryName: {
      type: String,
      default: ''
    },
    existingNames: {
      type: Array,
      default: () => []
    },
    isLoading: {
      type: Boolean,
      default: false
    }
  },

  validations() {
    return {
      form: {
        name: {
          required,
          unique: value => {
            if (!value) {
              return true
            }

            const normalized = String(value).toLowerCase()
            return !this.existingNames.some(n => String(n).toLowerCase() === normalized)
          }
        },
        minRpc: {},
        maxRpc: {}
      }
    }
  },

  data() {
    return {
      form: {
        name: '',
        status: 'Active',
        minRpc: null,
        maxRpc: null
      }
    }
  },

  computed: {
    ...mapState(['statusCodesForNewEntry']),

    showModal: {
      get() {
        return this.show
      },
      set(value) {
        this.$emit('update:show', value)
      }
    },

    boundsError() {
      const min = this.form.minRpc
      const max = this.form.maxRpc
      if (min == null && max == null) {
        return 'At least one of Min RPC / Max RPC must be set.'
      }

      if (min != null && max != null && Number(min) >= Number(max)) {
        return 'Min RPC must be less than Max RPC.'
      }

      return ''
    },

    nameInvalidFeedback() {
      if (this.$v.form.name.$dirty && !this.$v.form.name.required) {
        return 'Tier group name is required.'
      }

      if (this.$v.form.name.$dirty && !this.$v.form.name.unique) {
        return 'A tier group with this name already exists.'
      }

      return ''
    },

    canSubmit() {
      return !this.$v.form.name.$invalid && !this.boundsError && !this.isLoading
    }
  },

  watch: {
    show(value) {
      if (value) {
        this.resetForm()
      }
    }
  },

  methods: {
    resetForm() {
      this.form = {
        name: '',
        status: 'Active',
        minRpc: null,
        maxRpc: null
      }
      this.$v.$reset()
    },

    onSubmit() {
      this.$v.form.$touch()
      if (!this.canSubmit) {
        return
      }

      this.$emit('submit', {
        name: this.form.name,
        status: this.form.status,
        categoryId: this.categoryId,
        minRpc: this.form.minRpc,
        maxRpc: this.form.maxRpc
      })
    },

    closeModal() {
      this.showModal = false
    }
  }
}
</script>
